import ArrayDataStore from "csi/datastore/ArrayDataStore";
import { ComponentItem } from "designSystem/types/component-item-types";
import { ILocaleProvider } from "designSystem/locale";

/** Delayed Search select component placeholder id */
export const CSI_DROPDOWN_PLACEHOLDER = "CSI_DROPDOWN_PLACEHOLDER";

/** none component item id */
export const ALL_COMPONENT_ID = "ALL_COMPONENT_ID";
export const NONE_COMPONENT_ID = "NONE_COMPONENT_ID";
export const NO_MATCHES_COMPONENT_ID = "NO_MATCHES_COMPONENT_ID";

/** create instance of ComponentItem "All" */
export function getAllComponentItem(loc: ILocaleProvider): ComponentItem {
	return {
		id: ALL_COMPONENT_ID,
		name: loc.resource("SelectAll"),
	};
}

/** create instance of ComponentItem "None" */
export function getNoneComponentItem(loc: ILocaleProvider): ComponentItem {
	return {
		id: NONE_COMPONENT_ID,
		name: loc.resource("SelectNone"),
	};
}

/** create instance of ComponentItem "No Results" */
export function getNoMatchesComponentItem(): ComponentItem {
	return {
		id: NO_MATCHES_COMPONENT_ID,
		res: "NoMatches",
		disabled: true,
	};
}

/** default groupby function */
export function componentItemGroupByDefaultFn<T extends ComponentItem>(option: T): string {
	return option.group;
};

/** returns whether the component has values */
export function componentItemHasValues<T extends ComponentItem>(vals: T[]): boolean {
	return !!vals?.length;
};

/** check for none / empty item */
export function componentItemIsEmpty<T extends ComponentItem>(option: T): boolean {
	return option.id === NONE_COMPONENT_ID;
};

/** determine if given component item's name matches the given text */
export function componentItemMatchesText(item: ComponentItem, text: string, loc: ILocaleProvider): boolean {
	return loc.getComponentItemName(item)
		.toLocaleLowerCase()
		.includes(text.toLocaleLowerCase());
}

/** data store of component items */
export class ComponentItemDataStore<T extends ComponentItem> extends ArrayDataStore<T> {
	/** return data store with filtered set of items based on given search text */
	search = async (text: string, loc: ILocaleProvider): Promise<ComponentItemDataStore<T>> => {
		await this.load(0, Infinity);
		const data = this._data.filter((item) => componentItemMatchesText(item, text, loc));
		return new ComponentItemDataStore(data);
	};

	constructor(data: T[], loadCallback?: () => Promise<T[]>) {
		super(data, "id", loadCallback);
	}
}

/** get whether our option is disabled */
export function getComponentItemDisabled<T extends ComponentItem>(option: T): boolean {
	return option.disabled;
};

/** generic isOptionEqualToValue for mui autocomplete multiselect - if there is a value that there is also an option for, select it */
export function isComponentItemEqualToValue<T extends ComponentItem>(option: T, value: T): boolean {
	return option.id === value.id;
};
